/**
 * ReteContattiPage — la rubrica della rete personale (rotta /rete).
 *
 * Personal CRM del super utente: le persone conosciute presentando YumaOS, da
 * cui possono nascere collaborazioni, forniture, segnalazioni e opportunità di
 * lavoro. Strumento privato — nessun tenant lo vede, nessuna capability lo apre.
 *
 * Filtri per FACCETTA: è la resa del principio che i tag sono dimensioni
 * indipendenti che si incrociano (settore × tipo di rapporto × competenza), non
 * un mucchio unico di etichette.
 *
 * Doc canonico: docs/01-ARCHITECTURE/RETE-CONTATTI-MODEL.md
 * Estetica: coerente con PlatformLeadsPage / TenantsListPage.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, InputAdornment, Paper,
  Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Tooltip, Typography, alpha,
} from '@mui/material';
import { Edit, LocalOffer, PersonAdd, Refresh, Search } from '@mui/icons-material';
import { tokens } from '../theme';
import { NuovaPersonaDialog } from '../components/rete/NuovaPersonaDialog';
import { PersonaDetailDrawer } from '../components/rete/PersonaDetailDrawer';
import { TassonomiaDialog } from '../components/rete/TassonomiaDialog';
import {
  COLORE_FACCETTA_DEFAULT, formatDate, formatIsoDate, iniziali,
} from '../components/rete/labels';
import { listFaccette, listPersone, listTag } from '../services/reteContattiService';
import type { FaccettaRete, PersonaRete, TagRete } from '../types/reteContatti';

/** Quanti tag mostrare per faccetta prima del «+N» (il resto si espande). */
const MAX_CHIP_PER_FACCETTA = 6;

export const ReteContattiPage: React.FC = () => {
  const [persone, setPersone] = useState<PersonaRete[]>([]);
  const [faccette, setFaccette] = useState<FaccettaRete[]>([]);
  const [tag, setTag] = useState<TagRete[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /** Tag selezionati come filtro (AND tra faccette diverse, OR dentro la stessa). */
  const [filtroTag, setFiltroTag] = useState<string[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [tassonomiaOpen, setTassonomiaOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const showSnack = useCallback(
    (msg: string, severity: 'success' | 'error') => setSnack({ msg, severity }),
    [],
  );

  const caricaTassonomia = useCallback(async () => {
    const [f, t] = await Promise.all([listFaccette(), listTag()]);
    setFaccette(f);
    setTag(t);
  }, []);

  const caricaPersone = useCallback(async () => {
    setPersone(await listPersone());
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([caricaPersone(), caricaTassonomia()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento rete contatti');
    } finally {
      setLoading(false);
    }
  }, [caricaPersone, caricaTassonomia]);

  useEffect(() => { load(); }, [load]);

  const tagById = useMemo(() => new Map(tag.map((t) => [t.id, t])), [tag]);

  /**
   * Il "telaio": per ogni faccetta i tag effettivamente in uso + la COPERTURA
   * (quante persone sono classificate su quella dimensione).
   *
   * La copertura non è decorazione: dice dove la rubrica è cieca. Se su
   * "competenza" hai 3 persone su 20, quella dimensione non ti sta ancora
   * servendo a niente — ed è un'informazione che vuoi vedere.
   */
  const telaio = useMemo(() => {
    const usati = new Set<string>();
    for (const p of persone) for (const t of p.tag ?? []) usati.add(t);
    const tagPerFaccetta = new Map(faccette.map((f) => [f.id, [] as TagRete[]]));
    for (const t of tag) {
      if (t.stato !== 'attivo') continue;
      tagPerFaccetta.get(t.faccetta)?.push(t);
    }

    return faccette
      .map((faccetta) => {
        const suoi = (tagPerFaccetta.get(faccetta.id) ?? [])
          .filter((t) => usati.has(t.id))
          .sort((a, b) => (b.conteggio_uso ?? 0) - (a.conteggio_uso ?? 0));
        const slugFaccetta = new Set(suoi.map((t) => t.id));
        const coperte = persone.filter((p) => (p.tag ?? []).some((s) => slugFaccetta.has(s))).length;
        return { faccetta, tag: suoi, coperte };
      })
      .filter((riga) => riga.tag.length > 0);
  }, [persone, faccette, tag]);

  /**
   * Faccetta PORTANTE = quella con `ordine` più basso (nel seed è "tipo di
   * rapporto"). I suoi tag sono gli unici mostrati in lista, perché rispondono
   * alla domanda che conta davvero: perché questa persona è in rubrica.
   * La gerarchia sta nei dati: cambiare l'ordine cambia la colonna, senza deploy.
   */
  const faccettaPortante = faccette[0] ?? null;

  /** Faccette con la lista dei tag espansa (di default se ne mostrano poche). */
  const [faccetteEspanse, setFaccetteEspanse] = useState<string[]>([]);

  const filtrate = useMemo(() => {
    // I filtri sono in AND tra faccette diverse e in OR dentro la stessa faccetta:
    // "fotovoltaico E (fornitore O partner)" è la domanda che si fa davvero.
    const perFaccetta = new Map<string, string[]>();
    for (const slug of filtroTag) {
      const f = tagById.get(slug)?.faccetta ?? '';
      perFaccetta.set(f, [...(perFaccetta.get(f) ?? []), slug]);
    }

    return persone.filter((p) => {
      const suoi = p.tag ?? [];
      for (const gruppo of perFaccetta.values()) {
        if (!gruppo.some((slug) => suoi.includes(slug))) return false;
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        const haystack = [
          p.nome, p.azienda_testo, p.ruolo_testo, p.email, ...(p.telefoni ?? []).map((t) => t.numero),
          p.conosciuto_come, p.cosa_cerca, p.cosa_potrebbe_nascere, p.note_libere,
          ...suoi.map((s) => tagById.get(s)?.etichetta ?? ''),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [persone, filtroTag, search, tagById]);

  const proposteInAttesa = useMemo(
    () => tag.filter((t) => t.stato === 'proposto').length,
    [tag],
  );

  const selezionata = useMemo(
    () => persone.find((p) => p.id === selectedId) ?? null,
    [persone, selectedId],
  );

  const toggleFiltro = (slug: string) =>
    setFiltroTag((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));

  return (
    <Box>
      {/* HEADER ---------------------------------------------------------- */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: tokens.font.display, fontWeight: 600, fontSize: { xs: '1.6rem', md: '1.95rem' },
              lineHeight: 1.1, letterSpacing: '-0.02em', color: tokens.color.graphite,
              fontVariationSettings: '"WONK" 0, "SOFT" 30',
            }}
          >
            Rete contatti
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, maxWidth: 560 }}>
            Le persone conosciute lungo la strada: chi sono, cosa cercano, cosa
            potrebbe nascere. Racconta a voce, i tag li propone il sistema.
          </Typography>
        </Box>

        <Stack direction="row" spacing={3} useFlexGap alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1.5 }}>
          <HeaderMetric label="Persone" value={persone.length.toString()} />
          <HeaderMetric
            label="Tag attivi"
            value={tag.filter((t) => t.stato === 'attivo').length.toString()}
            accentColor={tokens.color.tierPro.main}
          />
          {proposteInAttesa > 0 && (
            <HeaderMetric
              label="Da approvare"
              value={proposteInAttesa.toString()}
              accentColor={tokens.color.warning}
            />
          )}
          <Tooltip title="Ricarica">
            <IconButton onClick={load} disabled={loading} size="small">
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<LocalOffer />}
            onClick={() => setTassonomiaOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Gestisci tag
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => setCreateOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Nuova persona
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {proposteInAttesa > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={(
            <Button size="small" onClick={() => setTassonomiaOpen(true)} sx={{ textTransform: 'none' }}>
              rivedi
            </Button>
          )}
        >
          {proposteInAttesa === 1
            ? 'C’è un tag proposto in attesa di approvazione.'
            : `Ci sono ${proposteInAttesa} tag proposti in attesa di approvazione.`}
        </Alert>
      )}

      {/* FILTRI ---------------------------------------------------------- */}
      <Paper
        variant="outlined"
        sx={{ p: 1.5, mb: 2, borderColor: tokens.color.line, bgcolor: tokens.color.paper }}
      >
        <Stack spacing={1.5}>
          <TextField
            placeholder="Cerca per nome, azienda, ruolo, contatto o contenuto delle note"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: tokens.color.mist }} />
                </InputAdornment>
              ),
              sx: { fontSize: '0.85rem' },
            }}
          />

          {telaio.map(({ faccetta, tag: suoi, coperte }) => {
            const colore = faccetta.colore || COLORE_FACCETTA_DEFAULT;
            const espansa = faccetteEspanse.includes(faccetta.id);
            const visibili = espansa ? suoi : suoi.slice(0, MAX_CHIP_PER_FACCETTA);
            const nascosti = suoi.length - visibili.length;

            return (
              <Stack
                key={faccetta.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 0.5, sm: 1.5 }}
                alignItems={{ sm: 'baseline' }}
                sx={{ pt: 0.75, borderTop: `1px solid ${tokens.color.lineSoft}` }}
              >
                {/* Etichetta + copertura: quante persone sono classificate qui */}
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{ minWidth: 168, flexShrink: 0 }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase', color: colore,
                    }}
                  >
                    {faccetta.etichetta}
                  </Typography>
                  <Tooltip
                    title={`${coperte} ${coperte === 1 ? 'persona' : 'persone'} classificate su questa dimensione`}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontFamily: tokens.font.mono, fontSize: '0.68rem',
                        color: colore, opacity: 0.75, cursor: 'default',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {coperte}
                    </Box>
                  </Tooltip>
                </Stack>

                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                  {visibili.map((t) => {
                    const attivo = filtroTag.includes(t.id);
                    return (
                      <Chip
                        key={t.id}
                        label={t.etichetta}
                        size="small"
                        onClick={() => toggleFiltro(t.id)}
                        sx={{
                          height: 28, fontSize: '0.75rem',
                          fontWeight: attivo ? 600 : 400,
                          cursor: 'pointer',
                          color: attivo ? colore : tokens.color.ash,
                          bgcolor: attivo ? alpha(colore, 0.14) : 'transparent',
                          border: `1px solid ${attivo ? alpha(colore, 0.45) : tokens.color.line}`,
                          '&:hover': { bgcolor: alpha(colore, attivo ? 0.2 : 0.07) },
                        }}
                      />
                    );
                  })}
                  {(nascosti > 0 || espansa) && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setFaccetteEspanse((prev) =>
                        espansa ? prev.filter((f) => f !== faccetta.id) : [...prev, faccetta.id])}
                      sx={{
                        border: 0, bgcolor: 'transparent', cursor: 'pointer',
                        fontFamily: tokens.font.body, fontSize: '0.72rem',
                        color: tokens.color.mist, px: 0.5, alignSelf: 'center',
                        '&:hover': { color: tokens.color.ash },
                      }}
                    >
                      {espansa ? 'meno' : `+${nascosti}`}
                    </Box>
                  )}
                </Stack>
              </Stack>
            );
          })}

          {filtroTag.length > 0 && (
            <Box>
              <Button
                size="small"
                onClick={() => setFiltroTag([])}
                sx={{ textTransform: 'none', color: tokens.color.ash }}
              >
                azzera filtri ({filtroTag.length})
              </Button>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* TABELLA --------------------------------------------------------- */}
      <Paper
        variant="outlined"
        sx={{ borderColor: tokens.color.line, bgcolor: tokens.color.paper, overflow: 'hidden' }}
      >
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
        ) : (
          <Table
            size="small"
            sx={{
              '& td': { py: 1.5 },
              '& tr:last-child td': { borderBottom: 0 },
              '& tbody tr': { transition: 'background-color 120ms ease', cursor: 'pointer' },
              '& tbody tr:hover': { backgroundColor: tokens.color.bone },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Persona</TableCell>
                <TableCell>
                  {/* Intestazione che dice cosa mostra la colonna: i tag della
                      faccetta portante, non tutti i tag della scheda. */}
                  {faccettaPortante ? 'Perché conta' : 'Tag'}
                </TableCell>
                <TableCell>Conosciuto</TableCell>
                <TableCell align="right">Note</TableCell>
                {/* Colonna azioni: senza etichetta, la matita si spiega da sé */}
                <TableCell align="right" sx={{ pr: 2, width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtrate.map((p) => {
                const suoi = p.tag ?? [];
                // Tessere in evidenza = faccetta portante; il resto conta come "+N".
                const inEvidenza = faccettaPortante
                  ? suoi.filter((s) => tagById.get(s)?.faccetta === faccettaPortante.id)
                  : suoi.slice(0, 2);
                const altri = suoi.length - inEvidenza.length;
                const colorePortante =
                  faccettaPortante?.colore || COLORE_FACCETTA_DEFAULT;

                return (
                  <TableRow key={p.id} onClick={() => setSelectedId(p.id)} hover>
                    <TableCell sx={{ minWidth: 210 }}>
                      <Stack direction="row" spacing={1.4} alignItems="center">
                        {/* Monogramma: dà un volto a chi non ha foto, con lo stesso
                            motivo del brand mark della barra in alto. */}
                        <Box
                          sx={{
                            width: 34, height: 34, borderRadius: 1.5, flexShrink: 0,
                            display: 'grid', placeItems: 'center',
                            bgcolor: tokens.color.lineSoft,
                            border: `1px solid ${tokens.color.line}`,
                            fontFamily: tokens.font.mono, fontSize: '0.72rem',
                            color: tokens.color.ash, letterSpacing: '0.02em',
                          }}
                        >
                          {iniziali(p.nome)}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: tokens.color.graphite, lineHeight: 1.25 }}
                          >
                            {p.nome}
                          </Typography>
                          <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                            {[p.ruolo_testo, p.azienda_testo].filter(Boolean).join(' · ') || '—'}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 340 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {suoi.length === 0 && (
                          <Typography variant="caption" sx={{ color: tokens.color.mist, fontStyle: 'italic' }}>
                            da classificare
                          </Typography>
                        )}
                        {inEvidenza.map((slug) => (
                          <Chip
                            key={slug}
                            label={tagById.get(slug)?.etichetta ?? slug}
                            size="small"
                            sx={{
                              height: 23, fontSize: '0.72rem',
                              bgcolor: alpha(colorePortante, 0.1), color: colorePortante,
                              border: `1px solid ${alpha(colorePortante, 0.26)}`,
                            }}
                          />
                        ))}
                        {altri > 0 && (
                          <Box
                            component="span"
                            sx={{
                              fontFamily: tokens.font.mono, fontSize: '0.72rem',
                              color: tokens.color.mist,
                            }}
                          >
                            +{altri}
                          </Box>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: tokens.color.ash }}>
                        {formatIsoDate(p.conosciuto_il)}
                      </Typography>
                      {p.conosciuto_come && (
                        <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                          {p.conosciuto_come}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Typography
                        sx={{
                          fontFamily: tokens.font.mono, fontSize: '0.8rem',
                          color: (p.conteggio_interazioni ?? 0) > 0
                            ? tokens.color.ash
                            : tokens.color.mist,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {p.conteggio_interazioni ?? 0}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: tokens.font.mono, fontSize: '0.72rem',
                          color: tokens.color.mist,
                        }}
                      >
                        {formatDate(p.updated_at ?? p.created_at)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right" sx={{ pr: 2 }}>
                      <Tooltip title="Apri la scheda e modifica">
                        <IconButton
                          size="small"
                          aria-label={`Apri la scheda di ${p.nome}`}
                          onClick={(e) => { e.stopPropagation(); setSelectedId(p.id); }}
                          sx={{
                            color: tokens.color.mist,
                            // Target confortevole anche col dito su tablet.
                            width: 34, height: 34,
                            '&:hover': { color: tokens.color.tierBusiness.main },
                          }}
                        >
                          <Edit sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtrate.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 7 }}>
                    {/* Stato vuoto come invito ad agire: è la prima schermata che
                        si vede, e deve dire cosa fare, non solo che non c'è nulla. */}
                    <Stack spacing={1.5} alignItems="center">
                      <Typography
                        sx={{
                          fontFamily: tokens.font.display, fontSize: '1.15rem', fontWeight: 500,
                          color: tokens.color.graphite, textAlign: 'center',
                        }}
                      >
                        {persone.length === 0
                          ? 'La rubrica è vuota.'
                          : 'Nessuna persona con questi filtri.'}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: tokens.color.ash, maxWidth: 420, textAlign: 'center' }}
                      >
                        {persone.length === 0
                          ? 'Aggiungi chi hai conosciuto — basta il nome. Poi apri la scheda e racconta a voce com’è andata: la nota e i tag li scrive il sistema.'
                          : 'Prova a togliere qualche filtro: le dimensioni si sommano tra loro.'}
                      </Typography>
                      {persone.length === 0 ? (
                        <Button
                          variant="contained"
                          startIcon={<PersonAdd />}
                          onClick={() => setCreateOpen(true)}
                          sx={{ textTransform: 'none', mt: 0.5 }}
                        >
                          Aggiungi la prima persona
                        </Button>
                      ) : (
                        <Button
                          onClick={() => setFiltroTag([])}
                          sx={{ textTransform: 'none', color: tokens.color.ash }}
                        >
                          azzera i filtri
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <NuovaPersonaDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async (id) => {
          setCreateOpen(false);
          showSnack('Scheda creata', 'success');
          await caricaPersone();
          setSelectedId(id);
        }}
      />

      <PersonaDetailDrawer
        persona={selezionata}
        tutte={persone}
        onApriPersona={setSelectedId}
        faccette={faccette}
        tag={tag}
        onClose={() => setSelectedId(null)}
        onSaved={caricaPersone}
        onTassonomiaCambiata={caricaTassonomia}
        showSnack={showSnack}
      />

      <TassonomiaDialog
        open={tassonomiaOpen}
        faccette={faccette}
        tag={tag}
        onClose={() => setTassonomiaOpen(false)}
        onCambiata={async () => { await Promise.all([caricaTassonomia(), caricaPersone()]); }}
        showSnack={showSnack}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack
          ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert>
          : <span />}
      </Snackbar>
    </Box>
  );
};

// ============================================================================
// HEADER METRIC — pill compatta, coerente con le altre pagine della console
// ============================================================================

const HeaderMetric: React.FC<{ label: string; value: string; accentColor?: string }> = ({
  label, value, accentColor,
}) => (
  <Box sx={{ borderLeft: `2px solid ${accentColor ?? tokens.color.line}`, pl: 1.5 }}>
    <Typography
      sx={{
        fontFamily: tokens.font.body, fontSize: '0.62rem', fontWeight: 600,
        letterSpacing: '0.1em', textTransform: 'uppercase', color: tokens.color.mist,
        lineHeight: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: tokens.font.display, fontSize: '1.15rem', fontWeight: 600,
        color: accentColor ?? tokens.color.graphite, lineHeight: 1.2, mt: 0.25,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Typography>
  </Box>
);
